/**
 * Mock School Log Service
 * 
 * This service provides mock data and simulated API calls for school logs.
 * It's used during development and testing when the real backend is not available.
 */

import { APIResponse } from "@/types";
import { SchoolLogModel, SchoolLogQuery } from "@/types/school-log.model";

// Simulated delay to mimic network latency (in milliseconds)
const MOCK_DELAY = 800;

// Mock data for school logs
const mockSchoolLogs: SchoolLogModel[] = Array.from({ length: 70 }, (_, i) => {
  // Generate random date within the last 30 days
  const today = new Date();
  const randomDaysAgo = Math.floor(Math.random() * 30);
  const date = new Date(today);
  date.setDate(today.getDate() - randomDaysAgo);
  
  // Format date as DD-MM-YYYY
  const loginDate = `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  
  // Same logout date as login date
  const logoutDate = loginDate;
  
  // Random login time between 8:00 AM and 11:00 AM
  const loginHour = Math.floor(Math.random() * 3) + 8;
  const loginMinute = Math.floor(Math.random() * 60);
  const loginTime = `${String(loginHour).padStart(2, '0')}:${String(loginMinute).padStart(2, '0')}pm`;
  
  // Random logout time between 3:00 PM and 6:00 PM
  const logoutHour = Math.floor(Math.random() * 3) + 3;
  const logoutMinute = Math.floor(Math.random() * 60);
  const logoutTime = `${String(logoutHour).padStart(2, '0')}:${String(logoutMinute).padStart(2, '0')}pm`;
  
  // Calculate session hours (roughly between 4 and 10 hours)
  const sessionHours = `${Math.floor(Math.random() * 6) + 4}hr ${Math.floor(Math.random() * 60)}min`;

  // School names
  const schoolNames = [
    "Shri Gnananda Matriculation School",
    "St. Joseph's High School",
    "Delhi Public School",
    "Kendriya Vidyalaya",
    "Ryan International School"
  ];
  
  // School admin names
  const adminNames = [
    "Mary Jenny Fernandez",
    "John Smith",
    "Priya Sharma",
    "Michael Johnson",
    "Aisha Khan"
  ];

  // Grades
  const grades = ["Grade A", "Grade B", "Grade C", "Grade D"];

  return {
    id: `school-log-${i + 1}`,
    slNo: `${(i + 1).toString().padStart(2, '0')}`,
    schoolName: schoolNames[Math.floor(Math.random() * schoolNames.length)],
    grade: grades[Math.floor(Math.random() * grades.length)],
    schoolAdminName: adminNames[Math.floor(Math.random() * adminNames.length)],
    emailAddress: `admin${i + 1}@school${i % 5 + 1}.edu`,
    contactNumber: `9${Math.floor(Math.random() * 10000000000).toString().padStart(10, '0')}`,
    ipAddress: `192.168.1.${Math.floor(Math.random() * 255)}`,
    geoLocation: "Chennai, India",
    loginDate,
    logoutDate,
    loginTime,
    logoutTime,
    sessionHours
  };
});

/**
 * Get all school logs with pagination and filtering
 */
export const getAllSchoolLogsService = async (
  query: SchoolLogQuery
): Promise<{
  data: APIResponse<SchoolLogModel[]>;
}> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY));
  
  const { page, pageSize, search, fromDate, toDate } = query;
  
  // Filter by search term if provided
  let filteredLogs = [...mockSchoolLogs];
  
  if (search) {
    const searchLower = search.toLowerCase();
    filteredLogs = filteredLogs.filter(log => 
      log.schoolName.toLowerCase().includes(searchLower) ||
      log.schoolAdminName.toLowerCase().includes(searchLower) ||
      log.emailAddress.toLowerCase().includes(searchLower) ||
      log.grade.toLowerCase().includes(searchLower)
    );
  }
  
  // Filter by date range if provided
  if (fromDate || toDate) {
    filteredLogs = filteredLogs.filter(log => {
      // Convert DD-MM-YYYY to Date object
      const [day, month, year] = log.loginDate.split('-').map(Number);
      const logDate = new Date(year, month - 1, day);
      
      if (fromDate && toDate) {
        return logDate >= fromDate && logDate <= toDate;
      } else if (fromDate) {
        return logDate >= fromDate;
      } else if (toDate) {
        return logDate <= toDate;
      }
      return true;
    });
  }
  
  // Calculate pagination
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);
  
  return {
    data: {
      data: paginatedLogs,
      pagination: {
        current_page: page,
        page: page,
        per_page: pageSize,
        pageSize: pageSize,
        total: filteredLogs.length,
        total_items: filteredLogs.length
      },
      message: "School logs fetched successfully",
    },
  };
};

/**
 * Get a single school log by ID
 */
export const getSchoolLogService = async (
  id: string
): Promise<{
  data: APIResponse<SchoolLogModel>;
}> => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY));
  
  // Find the school log by ID
  const schoolLog = mockSchoolLogs.find(log => log.id === id);
  
  if (!schoolLog) {
    throw new Error(`School log with ID ${id} not found`);
  }
  
  return {
    data: {
      data: schoolLog,
      message: "School log fetched successfully",
    },
  };
};
